import {
	Alert_2,
	ALERT_62,
	ALERT_71,
	AllocationMethodEnum,
	AssignmentFranchiseTypeEnum,
	AssignmentSettingUsedEnum,
	AssignmentStatusEnum,
	GenderEnum,
	GenderLabelEnum,
	MasterInfoEnum,
	ServerMessageCode,
	TextMessage,
} from '@/constant';
import {
	useCommonList,
	useFormikCustom,
	useRowSelection,
	useSearchParams,
} from '@/hooks';
import {
	ApiResponseEnum,
	ApiResponseError,
	GetDistrictValidatorResponse,
	ICommonApplicant,
	IProgram,
} from '@/services';
import {
	CommonActions,
	CommonSelectors,
	TCommonApplicantParams,
	useAppDispatch,
	useAppSelector,
	WorkerAssignmentActions,
	WorkerAssignmentSelectors,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import {
	hasAnyKey,
	insertObjectIfV2,
	NOOP,
	removeNilOrEmptyObjDeep,
	showAlert,
} from '@/utils';
import { Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import axios from 'axios';
import { omit, pick } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import {
	initialValuesFilter,
	mapPropsToValues,
	validationSchema,
} from './define-assignment.config';
import { formatDataToSubmit } from './define-assignment.helper';
import {
	DefineAssignmentType,
	SearchParamsDefineAssignment,
} from './define-assignment.type';

export const useLogic = () => {
	const dispatch = useAppDispatch();

	const paramsApplicantList = useAppSelector(
		CommonSelectors.selectParamsCommonApplicantList
	);

	const assignmentDetail = useSelector(WorkerAssignmentSelectors.selectDetail);
	const applicantList = useSelector(
		WorkerAssignmentSelectors.selectAssignmentApplicantList
	);

	const headerProgram = useSelector(CommonSelectors.selectHeaderProgram);

	const {
		commonList: programCommonList,
		selectMasterInfo: { data: programList },
	} = useCommonList(MasterInfoEnum.PROGRAM);

	const [searchParams] = useSearchParams();
	const [isAutomatic, setIsAutomatic] = useState(false);

	const _searchParams = searchParams as SearchParamsDefineAssignment;

	// const _isWorkerAlreadySelected = (selectedRowKeys: Key[]) => {
	// 	return selectedRowKeys.some((key) =>
	// 		rowSelection.selectedRowKeys.includes(key)
	// 	);
	// };

	const _isDistrictMismatch = (
		selectedRows: ICommonApplicant[],
		districtId: string | number | null
	) => {
		return selectedRows.some((item) => item.districtId !== districtId);
	};

	const sortRowsByIdDesc = (rows: ICommonApplicant[]) => {
		return [...rows].sort((a, b) => Number(b.id) - Number(a.id));
	};

	const getRowKeys = (rows: ICommonApplicant[]) => {
		return rows.map((row) => row.id);
	};

	const handleApplicantSelection = (
		selectedRowKeys: Key[],
		selectedRows: ICommonApplicant[]
	) => {
		const prevApplicantKeys =
			((applicantRowSelection?.selectedRowKeys as Key[]) || []) as Key[];
		const removedKeys = prevApplicantKeys.filter(
			(key) => !selectedRowKeys.includes(key)
		);
		const removedKeySet = new Set<Key>(removedKeys);

		const remainingRows = rowSelection.selectedRows.filter(
			(item) => !removedKeySet.has(item.id)
		);

		const mapById = new Map<number, ICommonApplicant>();
		remainingRows.forEach((r) => mapById.set(Number(r.id), r));
		selectedRows.forEach((r) => mapById.set(Number(r.id), r));

		const finalRows = Array.from(mapById.values());
		const sortedRows = sortRowsByIdDesc(finalRows);
		const sortedRowKeys = getRowKeys(sortedRows);

		handleSelectionChange(sortedRowKeys, sortedRows);

		applicantRowSelection.setState({
			selectedRowKeys,
			selectedRows,
		});
	};

	const rowSelection = useRowSelection<any, ICommonApplicant>({
		hideSelectAll: true,
		onChange: (selectedRowKeys: Key[], selectedRows: ICommonApplicant[]) => {
			const currentApplicantKeys =
				((applicantRowSelection?.selectedRowKeys as Key[]) || []) as Key[];
			const newApplicantKeys = currentApplicantKeys.filter((key) =>
				selectedRowKeys.includes(key)
			);
			const currentApplicantRows =
				((applicantRowSelection?.selectedRows as ICommonApplicant[]) ||
					[]) as ICommonApplicant[];
			const newApplicantRows = currentApplicantRows.filter((row) =>
				newApplicantKeys.includes(row.id)
			);
			applicantRowSelection.setState({
				selectedRowKeys: newApplicantKeys,
				selectedRows: newApplicantRows,
			});

			handleSelectionChange(selectedRowKeys, selectedRows);
			if (selectedRowKeys.length === 0) {
				formik.setValues((prev) => ({
					...prev,
					isPublicApplicant: false,
					fromApplicant: false,
					assignmentMethod: AllocationMethodEnum.MANUAL_ASSIGNMENT,
				}));
			}
		},
	});

	const applicantRowSelection = useRowSelection<any, ICommonApplicant>({
		onChange: handleApplicantSelection,
		getCheckboxProps: (record: ICommonApplicant) => ({
			disabled: record?.disabled,
		}),
		...insertObjectIfV2(_searchParams?.applicantIds?.length, {
			defaultSelectedRowKeys: _searchParams?.applicantIds || [],
			getCheckboxProps: (record: ICommonApplicant) => {
				const disabled = _searchParams?.applicantIds?.find(
					(id) => Number(id) === Number(record.id)
				)
					? false
					: record?.disabled;
				return {
					disabled,
				};
			},
		}),
	});

	const handleSelectionChange = (
		selectedRowKeys: Key[],
		selectedRows: ICommonApplicant[]
	) => {
		rowSelection.setState({ selectedRowKeys, selectedRows });
		formik.setFieldValue(
			'assignmentMethod',
			AllocationMethodEnum.MANUAL_ASSIGNMENT
		);
	};

	const { publicMainFranchiseId: publicFranchiseId } = useSelector(
		MyPageSelectors.selectDataMyPage
	);

	const selectedRowKeysDetail = useRef<Key[]>([]);

	const programHeaderSelected = useMemo(() => {
		return programCommonList.data?.find(
			(item) => Number((item as IProgram).id) === Number(headerProgram?.value)
		) as IProgram;
	}, [headerProgram, programCommonList.data]);

	const initialValues = useMemo(
		() =>
			mapPropsToValues(
				{
					..._searchParams,
				} as DefineAssignmentType,
				{
					assignmentDetail,
					publicFranchiseId,
					programId: headerProgram?.value,
					programStartDate: programHeaderSelected?.startDate,
					programEndDate: programHeaderSelected?.endDate,
				}
			),
		[
			_searchParams,
			assignmentDetail,
			publicFranchiseId,
			programHeaderSelected,
			headerProgram,
		]
	);

	const initialParamsState = useMemo(
		() => ({
			...pick(
				[
					'type',
					'franchiseId',
					'publicFranchiseId',
					'programId',
					'startDate',
					'endDate',
					'requestApplicant',
					'assignSetting',
				],
				initialValues
			),
			...pick(['fullName', 'assignmentPossibility'], initialValuesFilter),
		}),
		[initialValues]
	);

	const formik = useFormikCustom({
		enableReinitialize: true,
		initialValues,
		validationSchema: initialValues?.isDetail ? undefined : validationSchema,
		onSubmit(values) {
			const newSelection = (rowSelection.selectedRowKeys as Key[]).filter(
				(key) => !selectedRowKeysDetail.current.includes(key)
			);
			const removeIds = (selectedRowKeysDetail.current as Key[]).filter(
				(key) => !(rowSelection.selectedRowKeys as Key[]).includes(key)
			);

			const dataSubmit = formatDataToSubmit(
				values,
				newSelection as number[],
				removeIds as number[],
				_searchParams.assignmentfranchiseid
			);

			const createCommonAssignment = () => {
				dispatch(
					CommonActions.createCommonAssignmentRequest({
						data: dataSubmit,
						callback({ error }: { error: ApiResponseError }) {
							if (!axios.isAxiosError(error)) {
								handleSuccess(values.startDate);
								return;
							}

							const { messageCode, errorData } = error?.response?.data || {};
							const errorHandlers = {
								[ServerMessageCode.AM1_DUPLICATE_APPLICANT]: () =>
									handleApplicantError(TextMessage.ALERT_54, errorData),
								[ServerMessageCode.AM1_APPLICANT_NOT_POSSIBLE]: () =>
									handleApplicantError(TextMessage.ALERT_54, errorData),
							};

							const handler =
								errorHandlers[messageCode as keyof typeof errorHandlers];
							if (handler) {
								handler();
							}
						},
					})
				);
			};

			dispatch(
				CommonActions.getListCommonRequest({
					type: MasterInfoEnum.COMMON_DISTRICT_VALIDATOR,
					params: {
						...dataSubmit,
					},
					callback: (params) => {
						if (params?.error) {
							return;
						}

						const data = params?.data?.[0] as GetDistrictValidatorResponse;

						const am1InvalidDistrictIds = data?.AM1_INVALID_DISTRICT_ID;
						// const sysIdRecordHasAssignIds = data?.SYS_ID_RECORD_HAS_ASSIGN;

						if (am1InvalidDistrictIds?.length) {
							updateSelectedRowsWithError(am1InvalidDistrictIds);
							showAlert(
								ALERT_62(am1InvalidDistrictIds?.length),
								() => {
									// if (sysIdRecordHasAssignIds?.length) {
									// 	handleApplicantError(TextMessage.ALERT_63, {
									// 		errorIds: sysIdRecordHasAssignIds,
									// 	});
									// 	return;
									// }
									createCommonAssignment();
								},
								NOOP
							);
							return;
						}

						// if (sysIdRecordHasAssignIds?.length) {
						// 	handleApplicantError(TextMessage.ALERT_63, {
						// 		errorIds: sysIdRecordHasAssignIds,
						// 	});
						// 	return;
						// }

						createCommonAssignment();
					},
				})
			);
		},
	});

	const handleSuccess = (startDate: any) => {
		window.parent.postMessage({
			message: ApiResponseEnum.SUCCESS,
			values: startDate,
		});
	};

	const handleApplicantError = (alertMessage: string, errorData?: any) => {
		showAlert(alertMessage);
		if (errorData?.errorIds) {
			updateSelectedRowsWithError(errorData.errorIds);
		}
	};

	const updateSelectedRowsWithError = (errorIds: number[]) => {
		rowSelection.setState({
			selectedRows: rowSelection.selectedRows.map((item) => ({
				...item,
				hasError: errorIds.includes(item.id),
			})),
		});
	};

	const [params, setParams] =
		useState<TCommonApplicantParams>(initialParamsState);

	const shouldCallApi = useMemo(() => {
		const { assignmentMethod } = formik.values;

		const hasAssignmentMethod =
			assignmentMethod !== AllocationMethodEnum.NO_ASSIGNMENT;

		return hasAssignmentMethod && !!params.type;
	}, [formik.values, params]);

	const fetchData = useCallback(
		(params: TCommonApplicantParams) => {
			const { assignmentMethod, ...newParams } = params;

			const hasFullName = hasAnyKey(['fullName'], params)
				? params?.fullName
				: paramsApplicantList?.fullName;
			let excludeApplicantIds = hasFullName
				? []
				: (rowSelection.selectedRowKeys as number[]) || [];

			setParams((prev) => {
				setIsAutomatic(
					assignmentMethod === AllocationMethodEnum.AUTOMATIC_ASSIGNMENT
				);
				return {
					...prev,
					...newParams,
					excludeApplicantIds,
				};
			});
		},
		[rowSelection.selectedRowKeys, paramsApplicantList?.fullName]
	);

	const paramsAutomatic = useMemo(() => {
		return {
			...omit(['fullName', 'page', 'pageSize', 'sortSql', 'allData'], params),
		};
	}, [params]);

	const handleGenderAlert = (
		request: number,
		actual: number,
		gender: GenderLabelEnum
	) => {
		if (actual < request) {
			showAlert(ALERT_71(request, actual, gender));
			return true;
		}
		return false;
	};

	const checkGenderAssignments = (
		commonApplicants: ICommonApplicant[],
		maleRequest: number,
		femaleRequest: number
	) => {
		const maleAssignments = commonApplicants.filter(
			(item) => item.gender === GenderEnum.MALE
		).length;

		const femaleAssignments = commonApplicants.filter(
			(item) => item.gender === GenderEnum.FEMALE
		).length;

		if (maleAssignments < maleRequest && femaleAssignments < femaleRequest) {
			showAlert(
				ALERT_71(
					maleRequest + femaleRequest,
					maleAssignments + femaleAssignments,
					GenderLabelEnum.MALE_AND_FEMALE
				)
			);
			return;
		}

		handleGenderAlert(maleRequest, maleAssignments, GenderLabelEnum.MALE) ||
			handleGenderAlert(
				femaleRequest,
				femaleAssignments,
				GenderLabelEnum.FEMALE
			);
	};

	const handleSelectedRows = (
		commonApplicants: ICommonApplicant[],
		rowSelection: any
	) => {
		const selectedRowKeys = commonApplicants.map((item) => item.id);
		rowSelection.setState({
			selectedRowKeys,
			selectedRows: commonApplicants,
		});
		applicantRowSelection.setState({
			selectedRowKeys,
			selectedRows: commonApplicants,
		});
	};

	const {
		commonList: { fetching, data },
		fetchData: fetchDataAutomatic,
	} = useCommonList<ICommonApplicant>(
		MasterInfoEnum.COMMON_ASSIGNMENT_AUTO,
		false,
		removeNilOrEmptyObjDeep(paramsAutomatic),
		({ data }) => {
			if (data && data.length > 0) {
				const commonApplicants = data as ICommonApplicant[];
				const maleRequest = Number(formik.values.assignSetting?.maleRequest);
				const femaleRequest = Number(
					formik.values.assignSetting?.femaleRequest
				);

				if (isAutomatic) {
					checkGenderAssignments(commonApplicants, maleRequest, femaleRequest);
				}

				handleSelectedRows(commonApplicants, rowSelection);
			} else {
				if (formik.values?.isUsed === AssignmentSettingUsedEnum.USED) {
					showAlert(TextMessage.ALERT_79);
				}
				rowSelection.onReset();
				return;
			}
		}
	);

	useEffect(() => {
		if (shouldCallApi) {
			dispatch(
				CommonActions.getCommonApplicantListRequest({
					type: MasterInfoEnum.COMMON_APPLICANT,
					params: {
						excludeApplicantIds: _searchParams.applicantIds,
						...omit(['assignSetting'], params),
						pageSize: paramsApplicantList.pageSize,
						allData: false,
					},
				})
			);
		}
	}, [shouldCallApi, JSON.stringify(omit(['assignSetting'], params))]);

	useEffect(() => {
		if (isNotNilOrEmpty(formik.values.applicantIds)) {
			dispatch(
				CommonActions.getCommonApplicantWithIdsRequest({
					type: MasterInfoEnum.COMMON_APPLICANT,
					params: {
						assignmentPossibility: AssignmentStatusEnum.ASSIGNMENT_ALL,
						type: AssignmentFranchiseTypeEnum.COMMON,
						applicantIds: formik.values.applicantIds,
						pageSize: paramsApplicantList.pageSize,
						allData: false,
					},
					callback: ({ data }: { data: ICommonApplicant[] }) => {
						if (data?.length) {
							const selectedRowKeys = data.map((item) => item.id);
							rowSelection.setState({
								selectedRowKeys,
								selectedRows: data,
							});
						}
					},
				})
			);
		}
	}, [formik.values.applicantIds]);

	// fetch data mode detail
	useEffect(() => {
		if (_searchParams.assignmentfranchiseid) {
			dispatch(
				WorkerAssignmentActions.getWorkerAssignmentDetailRequest({
					id: Number(_searchParams.assignmentfranchiseid),
					isAudit: false,
				})
			);
		}
	}, [_searchParams.assignmentfranchiseid]);

	useEffect(() => {
		if (initialValues) {
			setParams(initialParamsState);
		}
	}, [JSON.stringify(initialValues)]);

	useEffect(() => {
		if (
			formik.values.assignmentMethod ===
				AllocationMethodEnum.AUTOMATIC_ASSIGNMENT &&
			params.franchiseId
		) {
			fetchDataAutomatic();
		}
	}, [
		formik.values.assignmentMethod,
		JSON.stringify(omit(['page', 'pageSize', 'allData', 'sortSql'], params)),
	]);

	const onSave = useCallback(async () => {
		try {
			// Validate form first
			const errors = await formik.validateForm(formik.values);
			if (isNotNilOrEmpty(errors)) {
				formik.handleSubmit();
				showAlert(TextMessage.ALERT_12);
				return;
			}

			const { assignmentMethod, requestApplicant, isDetail } = formik.values;
			const selectedCount = rowSelection.selectedRowKeys.length;
			const totalSelectedCount = isDetail
				? selectedCount + applicantList.length
				: selectedCount;

			// Check assignment conditions
			if (assignmentMethod !== AllocationMethodEnum.NO_ASSIGNMENT) {
				// Validate worker selection
				if (selectedCount === 0) {
					return showAlert(Alert_2('선택 된 근로자'));
				}

				// Confirm if selected workers less than requested
				const requestedCount = Number(requestApplicant);
				if (totalSelectedCount < requestedCount) {
					return showAlert(TextMessage.CONFIRM_35, formik.handleSubmit, NOOP, {
						cancelText: '아니요',
						confirmText: '예',
					});
				}
			}

			// Submit form if all validations pass
			await formik.onSubmit();
		} catch (error) {
			console.error('Error saving assignment:', error);
		}
	}, [formik.values, rowSelection.selectedRowKeys, applicantList]);

	return {
		formik,

		fetchData,
		onSave,
		params,
		rowSelection,
		applicantRowSelection,

		dateSourceAutomatic: data,
		fetchingAutomatic: fetching,

		_searchParams,
		programCommonList,
		programList,
	};
};
